/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { useState } from 'react';
import type { ClassModalProps, EditMutationProps } from './modules/AddEditClassModal';
import type { NewClassGet } from '@whizz/api-model';
import { useI18n } from '@whizz/react-i18n';
import { NewButton } from '../../../../global';
import { AddEditClassModal, ClassesTable } from './modules';
import { useModal } from '../../../../hooks/useModal';
import { QUERIES_IDS } from '../../../../util/vars';
import { useQuery } from 'react-query';
import { repository } from '@whizz/api-client';
import DeleteConfirmModal from './modules/DeleteConfirmModal';
import RosterManagementLayout from '../../../../layouts/RosterManagementLayout';
import Loader from '../../../../components/loader/Loader';
import { useSuccessErrorModal } from '../../../../hooks/useSuccessErrorModal';
import useYearGroups from '../../../../hooks/useYearGroups';
import ClassPictureLoginDownload from './modules/ClassPictureLoginDownload';
import { Alert } from '../../../../components';

const Classes = (): JSX.Element => {
  const { translate } = useI18n();
  const [ showInfo, setShowInfo ] = useState(false);
  const [ yearGroupsQuery ] = useYearGroups();

  const [ successErrorModal, toggleMessageType ] = useSuccessErrorModal();

  const [ addEditModal, toggleAddEditModal, setAddEditModalProps ] = useModal((props: ClassModalProps) => (
    <AddEditClassModal {...props} />
  ));

  const [ confirmModal, toggleConfirmModal, setConfirmModalProps ] = useModal((props: ClassModalProps) => (
    <DeleteConfirmModal {...props} />
  ));

  const onSuccess = () => toggleMessageType('success');
  const onError = () => toggleMessageType('error');

  const handleAdd = () => {
    setAddEditModalProps({
      title: translate('pages.ROSTER_MANAGEMENT.CLASSES.ADD_EDIT.ADD_TITLE'),
      data: null,
      yearGroups: yearGroupsQuery.data,
      classCount: classesQuery.data!.length,
      disableMaskClick: true,
      onSuccess,
      onError,
    });

    toggleAddEditModal(true);
  };

  const handleEdit = (data: EditMutationProps) => {
    setAddEditModalProps({
      title: translate('pages.ROSTER_MANAGEMENT.CLASSES.ADD_EDIT.EDIT_TITLE'),
      mode: 'edit',
      toggleConfirm: toggleConfirmModal,
      data: data,
      yearGroups: yearGroupsQuery.data,
      classCount: classesQuery.data!.length,
      disableMaskClick: true,
      onSuccess,
      onError,
    });

    setConfirmModalProps({
      title: translate('pages.ROSTER_MANAGEMENT.CLASSES.ADD_EDIT.DELETE_CONFIRM_TITLE'),
      toggle: toggleAddEditModal,
      toggleConfirm: toggleConfirmModal,
      data: data,
      yearGroups: yearGroupsQuery.data,
      disableMaskClick: true,
      onSuccess,
      onError,
    });

    toggleAddEditModal(true);
  };

  const classesQuery = useQuery(
    [ QUERIES_IDS.ROSTER_MANAGEMENT.CLASSES ],
    () => repository.NewClassRepository.getClasses({
      includeArchived: false,
      includeStudentCount: true,
      includeAccessCode: true,
    }),
    {
      enabled: true,
      refetchOnMount: 'always',
      placeholderData: [] as NewClassGet,
    },
  );

  const isLoading = !classesQuery.isFetched || classesQuery.isFetching;

  const Statistics = () => {
    if (isLoading) {
      return <Loader disableLabel smallSize />;
    }

    return (
      <>
        <span>{translate('pages.ROSTER_MANAGEMENT.CLASSES.STATISTICS.TOTAL_CLASSES')}: {classesQuery.data?.length}</span>
      </>
    );
  };

  return (
    <RosterManagementLayout
      statistics={<Statistics />}
    >
      {successErrorModal}
      {addEditModal}
      {confirmModal}
      <div className={'flex justify-between mb-6'}>
        <ClassPictureLoginDownload classes={classesQuery.data!} onMoreInfo={() => setShowInfo((prev) => !prev)}/>
        <NewButton
          disabled={!yearGroupsQuery.isSuccess}
          onClick={handleAdd}
          data-cy={'rm-new-class'}
        >
          {translate('pages.ROSTER_MANAGEMENT.CLASSES.BUTTONS.ADD')}
        </NewButton>
      </div>
      <Alert disableIcon show={showInfo}>
        <p>{translate('pages.ROSTER_MANAGEMENT.CLASSES.CLASS_PICTURE_LOGIN.INFO')}</p>
      </Alert>
      <ClassesTable
        isLoading={isLoading}
        classes={classesQuery.data!}
        onEdit={handleEdit}
      />
    </RosterManagementLayout>
  );
};

export default Classes;
